import { useEffect, useMemo, useRef } from 'react';
import { useDispatch } from 'react-redux';

import TimeTracking from '../../state/timeTracking';
import Debouncer from '../../helpers/Debouncer';

const DEBOUNCE_MS = 2000;

const sortedUserIdsKey = (ids = []) => (
  ids.length ? ids.slice().sort().join(',') : ''
);

export default function useLoadTimeEntries({
  userIds = [],
  startDate,
  endDate,
  getCardDetails = false,
  getRunningTasks = true,
  onLoaded,
} = {}) {
  const dispatch = useDispatch();
  const debouncerRef = useRef();
  if (!debouncerRef.current) {
    debouncerRef.current = new Debouncer();
  }

  const userIdsKey = useMemo(() => sortedUserIdsKey(userIds), [userIds]);
  const userIdsRef = useRef(userIds);
  userIdsRef.current = userIds;

  const extrasRef = useRef({ getCardDetails, getRunningTasks, onLoaded });
  extrasRef.current = { getCardDetails, getRunningTasks, onLoaded };

  const requestKey = startDate && endDate
    ? `${startDate}:${endDate}:${userIdsKey}:${getCardDetails}:${getRunningTasks}`
    : '';

  useEffect(() => {
    if (!requestKey) return undefined;

    debouncerRef.current.debounce(async () => {
      const ids = userIdsRef.current;
      const {
        getCardDetails: cardDetails,
        getRunningTasks: running,
        onLoaded: loaded,
      } = extrasRef.current;
      await dispatch(TimeTracking.getTimeEntries({
        startDate,
        endDate,
        getRunningTasks: running,
        userIds: ids,
        getCardDetails: cardDetails,
      }));
      if (loaded) await loaded();
    }, DEBOUNCE_MS);

    return () => {
      debouncerRef.current.clear();
    };
  }, [requestKey]);
}
